import type { TFunction } from 'i18next';

import { formatDate } from '@/lib/format';
import type { NotificationItem } from '@/services';

type Params = NotificationItem['params'];
type Part = (p: Params, t: TFunction) => string | null;

const str = (v: unknown): string | null => (v === undefined || v === null || v === '' ? null : String(v));
const date =
  (key: string): Part =>
  p => {
    const v = str(p[key]);
    return v ? formatDate(v) : null;
  };
const range: Part = p => {
  const from = str(p.from);
  const to = str(p.to);
  if (!from) return null;
  return to && to !== from ? `${formatDate(from)} – ${formatDate(to)}` : formatDate(from);
};
const incidentType: Part = (p, t) => {
  const v = str(p.incidentType);
  if (!v) return null;
  return v.startsWith('other:') ? v.slice(6) : t(`enum.incident_type.${v}`);
};
const severity: Part = (p, t) => {
  const v = str(p.severity);
  return v ? t(`enum.incident_severity.${v}`) : null;
};
const suspensionKind: Part = (p, t) => {
  const v = str(p.kind);
  return v ? t(`sst.suspensions.served_at_${v}`) : null;
};
const feverTemperature: Part = (p, t) => {
  const v = str(p.tempC);
  return v ? t('notifications.detail.temperature', { tempC: v }) : null;
};
const feverTime: Part = p => {
  const v = str(p.time);
  return v ? new Date(v).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : null;
};

const PARTS: Record<string, Part[]> = {
  'sst-incidents': [p => str(p.students), incidentType, severity],
  'lunch-detentions': [date('date')],
  suspensions: [range, suspensionKind],
  'leave-requests': [range],
  'it-tickets': [p => str(p.title)],
  fever: [feverTemperature, p => str(p.className), feverTime],
  'early-dismissals': [date('date')],
  'late-arrivals': [date('date')],
  'student-absences': [date('date')],
  'lesson-excusals': [date('date')],
};

/** Fever always carries a temperature, so it only counts as detail once a second part joins it. */
export function notificationDetail(item: NotificationItem, t: TFunction): string | null {
  const parts = (PARTS[item.category] ?? []).map(part => part(item.params, t)).filter((s): s is string => !!s);
  const informative = item.category === 'fever' ? parts.length > 1 : parts.length > 0;
  return informative ? parts.join(' · ') : null;
}
